import { type FormEvent, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import {
  createVaultItemEncrypted,
  updateVaultItemEncrypted,
  type VaultFlowDeps,
} from '../services/vaultFlow'
import {
  type VaultCustomField,
  type VaultItem,
  type VaultItemPayload,
  type VaultItemType,
  type VaultUri,
} from '../types'

/**
 * 新規 Vault アイテム作成フォームの state machine (T-D-Vault-B、xlsx No.28)。
 *
 * 設計判断:
 *   - UI とロジックの分離 (CLAUDE.md §6.8): state 遷移 / SK 取得 / 暗号化 /
 *     API 呼出 / エラー文言 key 決定 はこのフックに集約し、VaultNewPage は
 *     描画 + t(messageKey) + onChange ハンドラだけ持つ。
 *   - controlled state: 全フィールドを useState で持つ (uncontrolled だと
 *     type 切替時にフィールド値が失われる、また strength meter 等の派生 UI が
 *     書きづらい)。
 *   - type 切替時の挙動: 共通フィールド (name / notes / favorite) は残し、
 *     type 固有フィールド (login: url/username/password..., card: cardNumber...) は
 *     初期化する (CLAUDE.md §6.2 Simplicity: 切替 = 別アイテム扱い)。
 *   - 検証:
 *     - name は必須 (空文字 = invalid_name で submit 拒否)
 *     - cipher サイズは backend (CipherBlobDto MaxLength 12 KiB) で final 拒否
 *     - その他の field は backend が discriminate しないので形式チェックなし
 *   - 成功時: `navigate(targetRoute)` を呼んで navigation を caller に委ねる
 *     (デフォルトは作成後 `#vault` に戻る)。
 */

export type VaultItemFormState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; id: string }
  | { kind: 'error'; messageKey: string }

const ERROR_KEYS = {
  no_session: 'vault.error.no_session',
  invalid_name: 'vault.new.error.invalid_name',
  cipher_too_large: 'vault.new.error.cipher_too_large',
  network: 'vault.error.network',
  generic: 'vault.error.generic',
} as const

const messageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    // 413 が backend から返ることはないが、PayloadTooLargeException を将来 backend で
    // 入れる可能性があるため形だけ写像しておく。現状は 400 でも generic 扱い。
    if (err.status === 401) return ERROR_KEYS.no_session
    if (err.status === 413) return ERROR_KEYS.cipher_too_large
    if (err.status === 400) return ERROR_KEYS.cipher_too_large

    return ERROR_KEYS.network
  }

  return ERROR_KEYS.generic
}

/** 共通フィールド (全 type で持つ)。 */
export interface VaultItemFormCommon {
  name: string
  notes: string
  favorite: boolean
  /** #1 複数 URL (全 type 共通)。各 URI に match 方式を持てる。 */
  uris: VaultUri[]
}

/** type 固有フィールド (login: username / password / totpSecret)。 */
export interface VaultItemFormLogin {
  username: string
  password: string
  totpSecret: string
}

export interface VaultItemFormCard {
  cardNumber: string
  cardHolder: string
  cardExpiry: string
  cardCvv: string
}

export interface VaultItemFormNote {
  body: string
}

/** TASK-WEB-004: 名 + 姓 を 氏名 に統合し、住所 / ID 番号 / 生年月日 / 性別 を追加。
 *  UI 表示順は ID番号 → 氏名 → 性別 → 生年月日 → 住所 → 電話 → メール。 */
export interface VaultItemFormIdentity {
  idNumber: string
  fullName: string
  gender: string
  dateOfBirth: string
  address: string
  phone: string
  email: string
}

export interface VaultItemFormFields {
  common: VaultItemFormCommon
  login: VaultItemFormLogin
  card: VaultItemFormCard
  note: VaultItemFormNote
  identity: VaultItemFormIdentity
  /** #1 ユーザー定義カスタムフィールド (全 type 共通)。 */
  customFields: VaultCustomField[]
}

const emptyFields = (): VaultItemFormFields => ({
  common: { name: '', notes: '', favorite: false, uris: [] },
  login: { username: '', password: '', totpSecret: '' },
  card: { cardNumber: '', cardHolder: '', cardExpiry: '', cardCvv: '' },
  note: { body: '' },
  identity: {
    idNumber: '',
    fullName: '',
    gender: '',
    dateOfBirth: '',
    address: '',
    phone: '',
    email: '',
  },
  customFields: [],
})

/**
 * 既存 item の payload を form fields に展開 (F4 編集モード初期化用)。
 * payload に存在しない type 固有フィールドは empty defaults を返す。
 */
const fieldsFromItem = (item: VaultItem): VaultItemFormFields => {
  const out = emptyFields()
  const p = item.payload

  out.common = {
    name: p.name,
    notes: p.notes ?? '',
    favorite: p.favorite ?? false,
    // 全 type 共通の uris。復号時に正規化済 (旧 login.url → uris)。
    // form 編集が原 item を破壊しないよう deep copy。
    uris: p.uris ? p.uris.map(u => ({ ...u })) : [],
  }
  // #1 カスタムフィールドは全 type 共通。form 編集が原 item を破壊しないよう deep copy。
  out.customFields = p.fields ? p.fields.map(f => ({ ...f })) : []
  if (p.type === 'login') {
    out.login = {
      username: p.username ?? '',
      password: p.password ?? '',
      totpSecret: p.totpSecret ?? '',
    }
  } else if (p.type === 'card') {
    out.card = {
      cardNumber: p.cardNumber ?? '',
      cardHolder: p.cardHolder ?? '',
      cardExpiry: p.cardExpiry ?? '',
      cardCvv: p.cardCvv ?? '',
    }
  } else if (p.type === 'note') {
    out.note = { body: p.body }
  } else {
    // TASK-WEB-004: fullName 優先、無ければ legacy `lastName + ' ' + firstName` で
    // フォールバック合成 (既存 item は名/姓 別フィールドで保存されている)。
    // 5 新フィールド (idNumber / gender / dateOfBirth / address) は payload に
    // 無ければ空文字で初期化。
    const legacyFullName = [p.lastName, p.firstName].filter(Boolean).join(' ').trim()

    out.identity = {
      idNumber: p.idNumber ?? '',
      fullName: p.fullName ?? legacyFullName,
      gender: p.gender ?? '',
      dateOfBirth: p.dateOfBirth ?? '',
      address: p.address ?? '',
      phone: p.phone ?? '',
      email: p.email ?? '',
    }
  }

  return out
}

const buildPayload = (type: VaultItemType, fields: VaultItemFormFields): VaultItemPayload => {
  // #1 カスタムフィールド: 名前が空の行は捨てる。0 件なら undefined。
  const customFields: VaultCustomField[] = fields.customFields
    .map(f => ({ name: f.name.trim(), value: f.value, type: f.type }))
    .filter(f => f.name !== '')

  // 全 type 共通の URL リスト。空 URI 行は捨てる。match なしは match キーを省く。
  const uris: VaultUri[] = fields.common.uris
    .map(u => {
      const uri = u.uri.trim()

      return u.match ? { uri, match: u.match } : { uri }
    })
    .filter(u => u.uri !== '')

  const common = {
    name: fields.common.name.trim(),
    notes: fields.common.notes.trim() || undefined,
    favorite: fields.common.favorite,
    fields: customFields.length > 0 ? customFields : undefined,
    uris: uris.length > 0 ? uris : undefined,
  }

  if (type === 'login') {
    return {
      type: 'login',
      ...common,
      username: fields.login.username.trim() || undefined,
      password: fields.login.password || undefined,
      totpSecret: fields.login.totpSecret.trim() || undefined,
    }
  }
  if (type === 'card') {
    return {
      type: 'card',
      ...common,
      cardNumber: fields.card.cardNumber.trim() || undefined,
      cardHolder: fields.card.cardHolder.trim() || undefined,
      cardExpiry: fields.card.cardExpiry.trim() || undefined,
      cardCvv: fields.card.cardCvv.trim() || undefined,
    }
  }
  if (type === 'note') {
    return {
      type: 'note',
      ...common,
      body: fields.note.body,
    }
  }

  // TASK-WEB-004: 新規保存は fullName + 4 新フィールドのみ書き出す。
  // legacy firstName / lastName は schema に残るが新規 payload には含めない
  // (旧 item は normalize で fullName に統合済み、再保存時にも上書きしない)。
  return {
    type: 'identity',
    ...common,
    idNumber: fields.identity.idNumber.trim() || undefined,
    fullName: fields.identity.fullName.trim() || undefined,
    gender: fields.identity.gender.trim() || undefined,
    dateOfBirth: fields.identity.dateOfBirth.trim() || undefined,
    address: fields.identity.address.trim() || undefined,
    phone: fields.identity.phone.trim() || undefined,
    email: fields.identity.email.trim() || undefined,
  }
}

export interface UseVaultItemFormOptions {
  /** テスト用 DI: vaultFlow の deps を上書きする。 */
  flowDeps?: VaultFlowDeps

  /** テスト用 DI: 成功時に hash を書き換える navigate 関数 (既定: location.hash 直接書込)。 */
  navigate?: (target: string) => void

  /**
   * 編集モード (F4)。指定すると mount 時に既存 item の値で fields を埋め、
   * onSubmit で `updateVaultItemEncrypted(id, payload)` を呼ぶ (POST → PUT に切替)。
   *
   * 未指定 (undefined) なら従来通り create モード。
   */
  initialItem?: VaultItem

  /** 成功後に遷移する hash (既定: `'vault'`)。編集時は `vault/:id` に戻す等。 */
  successHash?: string
}

export interface UseVaultItemForm {
  state: VaultItemFormState
  isSubmitting: boolean
  type: VaultItemType
  setType: (t: VaultItemType) => void
  fields: VaultItemFormFields
  folderId: string | null
  setFolderId: (id: string | null) => void
  setCommon: <K extends keyof VaultItemFormCommon>(key: K, value: VaultItemFormCommon[K]) => void
  setLogin: <K extends keyof VaultItemFormLogin>(key: K, value: VaultItemFormLogin[K]) => void
  setCard: <K extends keyof VaultItemFormCard>(key: K, value: VaultItemFormCard[K]) => void
  setNote: <K extends keyof VaultItemFormNote>(key: K, value: VaultItemFormNote[K]) => void
  setIdentity: <K extends keyof VaultItemFormIdentity>(
    key: K,
    value: VaultItemFormIdentity[K],
  ) => void

  /** #1 login URL の追加 / 削除 / 更新。 */
  addUri: () => void
  removeUri: (index: number) => void
  updateUri: (index: number, patch: Partial<VaultUri>) => void

  /** #1 カスタムフィールドの追加 / 削除 / 更新。 */
  addCustomField: () => void
  removeCustomField: (index: number) => void
  updateCustomField: (index: number, patch: Partial<VaultCustomField>) => void

  /** #4: 保存成功後に保管庫をロックする (新規作成画面のチェックボックス用)。 */
  lockAfterSave: boolean
  setLockAfterSave: (v: boolean) => void

  onSubmit: (ev: FormEvent<HTMLFormElement>) => Promise<void>
}

const defaultNavigate = (target: string): void => {
  if (typeof window === 'undefined') return
  const next = target.startsWith('#') ? target : `#${target}`
  if (window.location.hash === next) return
  window.location.hash = next
}

export const useVaultItemForm = (options: UseVaultItemFormOptions = {}): UseVaultItemForm => {
  const initialItem = options.initialItem
  const isEditMode = initialItem !== undefined

  const [state, setState] = useState<VaultItemFormState>({ kind: 'idle' })
  const [type, setTypeInternal] = useState<VaultItemType>(initialItem?.type ?? 'login')

  const [fields, setFields] = useState<VaultItemFormFields>(() =>
    initialItem ? fieldsFromItem(initialItem) : emptyFields(),
  )

  const [folderId, setFolderId] = useState<string | null>(initialItem?.folderId ?? null)
  // #4: 保存後に保管庫をロックするか (新規作成画面のチェックボックス)。
  const [lockAfterSave, setLockAfterSave] = useState(false)
  const submittingRef = useRef(false)

  const navigate = options.navigate ?? defaultNavigate
  const successHash = options.successHash ?? 'vault'

  const setType = (next: VaultItemType): void => {
    // 共通フィールド + カスタムフィールドは保持、type 固有フィールドはリセット
    setFields(prev => {
      const fresh = emptyFields()

      return { ...fresh, common: prev.common, customFields: prev.customFields }
    })
    setTypeInternal(next)
  }

  const setCommon = <K extends keyof VaultItemFormCommon>(
    key: K,
    value: VaultItemFormCommon[K],
  ): void => {
    setFields(prev => ({ ...prev, common: { ...prev.common, [key]: value } }))
  }

  const setLogin = <K extends keyof VaultItemFormLogin>(
    key: K,
    value: VaultItemFormLogin[K],
  ): void => {
    setFields(prev => ({ ...prev, login: { ...prev.login, [key]: value } }))
  }

  const setCard = <K extends keyof VaultItemFormCard>(
    key: K,
    value: VaultItemFormCard[K],
  ): void => {
    setFields(prev => ({ ...prev, card: { ...prev.card, [key]: value } }))
  }

  const setNote = <K extends keyof VaultItemFormNote>(
    key: K,
    value: VaultItemFormNote[K],
  ): void => {
    setFields(prev => ({ ...prev, note: { ...prev.note, [key]: value } }))
  }

  const setIdentity = <K extends keyof VaultItemFormIdentity>(
    key: K,
    value: VaultItemFormIdentity[K],
  ): void => {
    setFields(prev => ({ ...prev, identity: { ...prev.identity, [key]: value } }))
  }

  // 全 type 共通の URL リスト編集ヘルパ (uris は common に移動済)。
  const addUri = (): void => {
    setFields(prev => ({
      ...prev,
      common: { ...prev.common, uris: [...prev.common.uris, { uri: '' }] },
    }))
  }

  const removeUri = (index: number): void => {
    setFields(prev => ({
      ...prev,
      common: { ...prev.common, uris: prev.common.uris.filter((_, i) => i !== index) },
    }))
  }

  const updateUri = (index: number, patch: Partial<VaultUri>): void => {
    setFields(prev => ({
      ...prev,
      common: {
        ...prev.common,
        uris: prev.common.uris.map((u, i) => (i === index ? { ...u, ...patch } : u)),
      },
    }))
  }

  // #1 カスタムフィールドリストの編集ヘルパ。
  const addCustomField = (): void => {
    setFields(prev => ({
      ...prev,
      customFields: [...prev.customFields, { name: '', value: '', type: 'text' }],
    }))
  }

  const removeCustomField = (index: number): void => {
    setFields(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }))
  }

  const updateCustomField = (index: number, patch: Partial<VaultCustomField>): void => {
    setFields(prev => ({
      ...prev,
      customFields: prev.customFields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    }))
  }

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    if (submittingRef.current) return

    if (!fields.common.name.trim()) {
      setState({ kind: 'error', messageKey: ERROR_KEYS.invalid_name })

      return
    }

    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      setState({ kind: 'error', messageKey: ERROR_KEYS.no_session })

      return
    }

    submittingRef.current = true
    setState({ kind: 'submitting' })

    try {
      const payload = buildPayload(type, fields)

      const item = isEditMode
        ? await updateVaultItemEncrypted(
            session.symmetricKey,
            initialItem.id,
            payload,
            folderId,
            options.flowDeps,
          )
        : await createVaultItemEncrypted(session.symmetricKey, payload, folderId, options.flowDeps)

      setState({ kind: 'success', id: item.id })

      if (lockAfterSave) {
        // #4: 保存完了後に保管庫をロックし、マスターパスワード再入力を促す。
        // create は既に完了済みなので、ここで SK を zeroize しても影響しない。
        sessionStore.lock()
        navigate('lock')
      } else {
        navigate(successHash)
      }
    } catch (err) {
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    } finally {
      submittingRef.current = false
    }
  }

  return {
    state,
    isSubmitting: state.kind === 'submitting',
    type,
    setType,
    fields,
    folderId,
    setFolderId,
    setCommon,
    setLogin,
    setCard,
    setNote,
    setIdentity,
    addUri,
    removeUri,
    updateUri,
    addCustomField,
    removeCustomField,
    updateCustomField,
    lockAfterSave,
    setLockAfterSave,
    onSubmit,
  }
}
